import React, { useEffect, useRef } from 'react';
import styles from '../../Appointment.module.css';
import PatientHistory from '../patientHistory/PatientHistory';
import AnamneseHistory from '../anamneseHistory/AnamneseHistory';
import MainPain from '../mainPain/MainPain';
import Documents from '../documents/Documents';
import Entry from '../entry/Entry';
import CranialAsymmetry from '../cranialAsymmetry/CranialAsymmetry';
import GrowthCurve from '../growthCurve/GrowthCurve';
import MotorMilestones from '../motorMilestones/MotorMilestones';
import PatientAttachments from '../patientAttachments/PatientAttachments';
import Notes from '../notes/Notes';
import BodyMeasurements from '../bodyMeasurements/BodyMeasurements';
import EvolutionPhotos from '../evolutionPhotos/EvolutionPhotos';
import BodyMap from '../bodyMap/BodyMap';
import MealPlan from '../mealPlan/MealPlan';
import ApplicationMap from '../applicationMap/ApplicationMap';
import Thermography from '../thermography/Thermography';
import Aims from '../aims/Aims';
import Diagnoses from '../diagnoses/Diagnoses';
import ModuleNav from '../moduleNav/ModuleNav';
import OtherRecords from '../otherRecords/OtherRecords';
import ScaleModule from '../scales/ScaleModule';
import { MODULE_KEYS } from '../../modules/appointmentModules';
import { getScale } from '../../modules/scales';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import ChildCareOutlinedIcon from '@mui/icons-material/ChildCareOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import AccessibilityOutlinedIcon from '@mui/icons-material/AccessibilityOutlined';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import VaccinesOutlinedIcon from '@mui/icons-material/VaccinesOutlined';
import DeviceThermostatOutlinedIcon from '@mui/icons-material/DeviceThermostatOutlined';
import AccessibilityNewOutlinedIcon from '@mui/icons-material/AccessibilityNewOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';

// Identidade estável para quando o paciente não tem anexos: um `|| []` inline
// criaria um array novo a cada render (o cronômetro do atendimento re-renderiza
// a página a cada segundo), disparando à toa o efeito de `attachments` do card
// de assimetria craniana.
const EMPTY_ATTACHMENTS = [];
const EMPTY_GROWTH = [];
const EMPTY_MILESTONES = [];
const EMPTY_THERMOGRAPHY = [];
const EMPTY_AIMS = [];
const EMPTY_DIAGNOSES = [];

/**
 * Corpo da tela de atendimento: menu lateral com um atalho por card e a coluna
 * de cards, um por módulo visível. Queixas e o registro de queixa/evolução (a
 * área de trabalho da consulta) vêm primeiro; os demais módulos seguem na
 * ordem do tipo. Sem o módulo de queixa/evolução, o card de anotações assume
 * o lugar dele no topo. Módulos fora do tipo só com registros antigos
 * (`otherModules`) viram atalhos no fim da coluna; o clique abre o card.
 */
const AppointmentModules = ({
  modules,
  data,
  enabled,
  initialComplaints,
  onComplaintsChange,
  entryRef,
  onAssessmentChange,
  onEvolutionChange,
  onReload,
  onNoteChange,
  companyConfig,
  appointmentType,
  otherModules = [],
  onOpenModule,
}) => {
  // Módulo aberto pelo atalho: rola até o card assim que ele aparece
  const scrollToKeyRef = useRef(null);

  const appointment = data?.appointment || {};
  const patient = data?.patient || {};
  const show = (key) => modules.includes(key);
  const notesOnTop = show(MODULE_KEYS.NOTES) && !show(MODULE_KEYS.EVOLUTION);

  function notesCard() {
    return {
      id: 'module-notes',
      label: 'Anotações',
      accent: 'amber',
      icon: <StickyNote2OutlinedIcon />,
      element: (
        <Notes
          list={data?.notes || []}
          appointmentId={appointment.id || ''}
          patientId={patient.id || ''}
          enabled={enabled}
          collapsible={!notesOnTop}
          onChange={onNoteChange}
          onSaved={onReload}
          patientName={patient.name}
          professionalId={appointment.resource}
          companyConfig={companyConfig}
        />
      ),
    };
  }

  // Escalas clínicas: um card genérico por escala do catálogo
  function scaleCard(scale) {
    if (!appointment.id || !patient.id) return null;
    const Icon = scale.icon || TuneOutlinedIcon;
    return {
      id: `module-scale-${scale.key}`,
      label: scale.name,
      accent: scale.accent || 'teal',
      icon: <Icon />,
      element: (
        <ScaleModule
          scale={scale}
          data={data?.scaleAssessments || []}
          patientId={patient.id}
          appointmentId={appointment.id}
          appointmentDate={appointment.date || ''}
          gender={patient.gender}
          collapsible={true}
        />
      ),
    };
  }

  function bottomCard(key) {
    switch (key) {
      case MODULE_KEYS.NOTES:
        return notesOnTop ? null : notesCard();
      case MODULE_KEYS.EVOLUTION:
        return {
          id: 'module-evolution',
          label: 'Evolução',
          accent: 'blue',
          icon: <AssignmentOutlinedIcon />,
          element: (
            <PatientHistory
              data={data?.history || []}
              enabled={enabled}
              collapsible={true}
              patientId={patient.id || ''}
              onRecordUpdated={onReload}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
            />
          ),
        };
      case MODULE_KEYS.CRANIAL_ASYMMETRY:
        if (!appointment.id || !patient.id) return null;
        return {
          id: 'module-cranial',
          label: 'Assimetria craniana',
          accent: 'teal',
          icon: <MonitorHeartOutlinedIcon />,
          element: (
            <CranialAsymmetry
              data={data?.cranialAsymetry || {}}
              attachments={data?.patientAttachments || EMPTY_ATTACHMENTS}
              appointmentId={appointment.id}
              patientId={patient.id}
              currentDate={appointment.date || ''}
              onUpdated={onReload}
              enabled={true}
              collapsible={true}
              patientName={patient.name || ''}
              companyData={companyConfig}
            />
          ),
        };
      case MODULE_KEYS.GROWTH_CURVE:
        if (!appointment.id || !patient.id) return null;
        return {
          id: 'module-growth',
          label: 'Curva de crescimento',
          accent: 'teal',
          icon: <ShowChartOutlinedIcon />,
          element: (
            <GrowthCurve
              data={data?.growthMeasurements || EMPTY_GROWTH}
              cranial={data?.cranialAsymetry}
              patientId={patient.id}
              appointmentId={appointment.id}
              appointmentDate={appointment.date || ''}
              birthday={patient.birthday}
              gender={patient.gender}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
              onUpdated={onReload}
              enabled={true}
              collapsible={true}
            />
          ),
        };
      case MODULE_KEYS.MOTOR_MILESTONES:
        if (!appointment.id || !patient.id) return null;
        return {
          id: 'module-motor-milestones',
          label: 'Marcos motores',
          accent: 'teal',
          icon: <ChildCareOutlinedIcon />,
          element: (
            <MotorMilestones
              data={data?.motorMilestones || EMPTY_MILESTONES}
              birthInfo={data?.birthInfo || null}
              patientId={patient.id}
              appointmentId={appointment.id}
              appointmentDate={appointment.date || ''}
              birthday={patient.birthday}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
              onUpdated={onReload}
              enabled={true}
              collapsible={true}
            />
          ),
        };
      case MODULE_KEYS.AIMS:
        if (!appointment.id || !patient.id) return null;
        return {
          id: 'module-aims',
          label: 'Escala de Alberta',
          accent: 'teal',
          icon: <AccessibilityNewOutlinedIcon />,
          element: (
            <Aims
              data={data?.aimsAssessments || EMPTY_AIMS}
              birthInfo={data?.birthInfo || null}
              patientId={patient.id}
              appointmentId={appointment.id}
              appointmentDate={appointment.date || ''}
              birthday={patient.birthday}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
              onUpdated={onReload}
              enabled={true}
              collapsible={true}
            />
          ),
        };
      case MODULE_KEYS.BODY_MEASUREMENTS:
        if (!appointment.id || !patient.id) return null;
        return {
          id: 'module-body',
          label: 'Medidas corporais',
          accent: 'green',
          icon: <StraightenOutlinedIcon />,
          element: (
            <BodyMeasurements
              data={data?.bodyMeasurements || []}
              patientId={patient.id}
              appointmentId={appointment.id}
              gender={patient.gender}
              birthday={patient.birthday}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
              onUpdated={onReload}
              enabled={true}
              collapsible={true}
            />
          ),
        };
      case MODULE_KEYS.EVOLUTION_PHOTOS:
        if (!patient.id) return null;
        return {
          id: 'module-evolution-photos',
          label: 'Fotos de evolução',
          accent: 'purple',
          icon: <PhotoLibraryOutlinedIcon />,
          element: (
            <EvolutionPhotos
              attachments={data?.patientAttachments || EMPTY_ATTACHMENTS}
              patientId={patient.id}
              appointmentId={appointment.id}
              currentDate={appointment.date || ''}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
              onUpdated={onReload}
              enabled={true}
              collapsible={true}
            />
          ),
        };
      case MODULE_KEYS.BODY_MAP:
        if (!appointment.id || !patient.id) return null;
        return {
          id: 'module-body-map',
          label: 'Mapa corporal',
          accent: 'pink',
          icon: <AccessibilityOutlinedIcon />,
          element: (
            <BodyMap
              data={data?.bodyMap || []}
              patientId={patient.id}
              appointmentId={appointment.id}
              gender={patient.gender}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
              enabled={true}
              collapsible={true}
            />
          ),
        };
      case MODULE_KEYS.APPLICATION_MAP:
        if (!appointment.id || !patient.id) return null;
        return {
          id: 'module-application-map',
          label: 'Mapa de aplicação',
          accent: 'purple',
          icon: <VaccinesOutlinedIcon />,
          element: (
            <ApplicationMap
              data={data?.applicationMaps || []}
              products={data?.applicationProducts || []}
              patientId={patient.id}
              appointmentId={appointment.id}
              gender={patient.gender}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
              enabled={true}
              currentDate={appointment.date || ''}
              collapsible={true}
            />
          ),
        };
      case MODULE_KEYS.THERMOGRAPHY:
        if (!appointment.id || !patient.id) return null;
        return {
          id: 'module-thermography',
          label: 'Termografia',
          accent: 'amber',
          icon: <DeviceThermostatOutlinedIcon />,
          element: (
            <Thermography
              data={data?.thermography || EMPTY_THERMOGRAPHY}
              patientId={patient.id}
              appointmentId={appointment.id}
              appointmentDate={appointment.date || ''}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
              onUpdated={onReload}
              enabled={true}
              collapsible={true}
            />
          ),
        };
      case MODULE_KEYS.MEAL_PLAN:
        if (!appointment.id || !patient.id) return null;
        return {
          id: 'module-meal-plan',
          label: 'Plano alimentar',
          accent: 'amber',
          icon: <RestaurantMenuOutlinedIcon />,
          element: (
            <MealPlan
              data={data?.mealPlans || []}
              bodyMeasurements={data?.bodyMeasurements || []}
              patientId={patient.id}
              appointmentId={appointment.id}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
              enabled={true}
              collapsible={true}
              onUpdated={onReload}
            />
          ),
        };
      case MODULE_KEYS.ANAMNESIS:
        return {
          id: 'module-anamnesis',
          label: 'Anamnese',
          accent: 'purple',
          icon: <HistoryEduOutlinedIcon />,
          element: (
            <AnamneseHistory
              patientId={patient.id || ''}
              appointmentId={appointment.id || ''}
              anamnesisTemplateId={appointmentType?.Config?.anamnesis?.templateId || null}
              currentDate={appointment.date || ''}
              list={data?.anamneses || []}
              onUpdated={onReload}
              enabled={true}
              collapsible={true}
              patientName={patient.name}
              professionalId={appointment.resource}
            />
          ),
        };
      case MODULE_KEYS.DIAGNOSES:
        if (!appointment.id || !patient.id) return null;
        return {
          id: 'module-diagnoses',
          label: 'Diagnósticos (CID)',
          accent: 'pink',
          icon: <LocalHospitalOutlinedIcon />,
          element: (
            <Diagnoses
              data={data?.diagnoses || EMPTY_DIAGNOSES}
              patientId={patient.id}
              appointmentId={appointment.id}
              patientName={patient.name}
              professionalId={appointment.resource}
              companyConfig={companyConfig}
              onUpdated={onReload}
              enabled={true}
              collapsible={true}
            />
          ),
        };
      case MODULE_KEYS.DOCUMENTS:
        return {
          id: 'module-documents',
          label: 'Documentos',
          accent: 'blue',
          icon: <DescriptionOutlinedIcon />,
          element: (
            <Documents
              patientData={patient}
              list={data?.documents || []}
              prescriptions={data?.prescriptions || []}
              prescriptionProps={{
                patientId: patient.id,
                patientName: patient.name,
                appointmentId: appointment.id,
                appointmentDate: appointment.date || '',
                professionalId: appointment.resource,
                templates: data?.prescriptionTemplates || [],
                onUpdated: onReload,
              }}
              collapsible={true}
            />
          ),
        };
      case MODULE_KEYS.ATTACHMENTS:
        if (!patient.id) return null;
        return {
          id: 'module-attachments',
          label: 'Arquivos',
          accent: 'green',
          icon: <FolderOutlinedIcon />,
          element: (
            <PatientAttachments
              list={data?.patientAttachments}
              patientId={patient.id}
              appointmentId={appointment.id || ''}
              currentDate={appointment.date || ''}
              enabled={true}
              collapsible={true}
            />
          ),
        };
      default: {
        // complaints tem lugar próprio; chaves desconhecidas não renderizam
        const scale = getScale(key);
        return scale ? scaleCard(scale) : null;
      }
    }
  }

  const cards = [];

  if (show(MODULE_KEYS.COMPLAINTS)) {
    cards.push({
      id: 'module-complaints',
      label: 'Queixas principais',
      accent: 'pink',
      icon: <MedicalInformationOutlinedIcon />,
      element: (
        <MainPain
          list={initialComplaints}
          history={data?.history || []}
          onChange={onComplaintsChange}
          enabled={true}
          patientName={patient.name}
          professionalId={appointment.resource}
          companyConfig={companyConfig}
        />
      ),
    });
  }

  if (enabled && show(MODULE_KEYS.EVOLUTION) && appointment.id) {
    cards.push({
      id: 'module-entry',
      label: 'Registro',
      accent: 'blue',
      icon: <EditNoteOutlinedIcon />,
      element: <Entry ref={entryRef} appointmentId={appointment.id} onAssesmentChange={onAssessmentChange} onEvolutionChange={onEvolutionChange} />,
    });
  }

  if (notesOnTop && appointment.id) {
    cards.push(notesCard());
  }

  modules.forEach((key) => {
    const card = bottomCard(key);
    if (card) cards.push({ ...card, moduleKey: key });
  });

  const otherItems = otherModules
    .map((item) => {
      const card = bottomCard(item.key);
      return card ? { key: item.key, label: card.label, icon: card.icon, accent: card.accent, count: item.count } : null;
    })
    .filter(Boolean);

  function openModule(key) {
    scrollToKeyRef.current = key;
    if (onOpenModule) onOpenModule(key);
  }

  useEffect(() => {
    const key = scrollToKeyRef.current;
    if (!key) return;
    const card = cards.find((item) => item.moduleKey === key);
    const element = card ? document.getElementById(card.id) : null;
    if (!element) return;
    scrollToKeyRef.current = null;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  const withNav = cards.length > 1;

  return (
    <div className={`${styles.layout} ${withNav ? '' : styles.layoutNoNav}`}>
      {withNav && <ModuleNav sections={cards} />}

      <div className={styles.modules}>
        {cards.map((card) => (
          <div key={card.id} id={card.id} className={styles.anchor}
            data-tour={card.id === 'module-entry' ? 'appointment-entry' : undefined}>
            {card.element}
          </div>
        ))}
        <OtherRecords items={otherItems} onOpen={openModule} />
      </div>
    </div>
  );
};

export default AppointmentModules;
